import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { api } from '@/lib/api'
import { getAdminToken } from '@/lib/admin-store'
import {
  applyAdminFilters,
  getAdminNavLinks,
  readAdminFiltersFromSearch,
  type AdminFilters,
} from './-admin-utils'
import styles from './admin.module.css'

export const Route = createFileRoute('/admin/users')({
  component: AdminUsersRoute,
})

function AdminUsersRoute() {
  const [token] = useState(getAdminToken)
  const [filters, setFilters] = useState<AdminFilters>(readAdminFiltersFromSearch)
  const [rows, setRows] = useState<Awaited<ReturnType<typeof api.adminUsers>>['items']>([])
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const navLinks = useMemo(() => getAdminNavLinks(filters), [filters])

  useEffect(() => {
    const load = async (): Promise<void> => {
      if (!token) {
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const data = await api.adminUsers(token, {
          limit: 200,
          ...(filters.query ? { q: filters.query } : {}),
        })
        setRows(data.items)
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : 'Failed to load users.')
      } finally {
        setIsLoading(false)
      }
    }

    void load()
  }, [filters.query, token])

  const filteredRows = useMemo(() => {
    let next = rows
    if (filters.country.trim()) {
      const country = filters.country.trim().toUpperCase()
      next = next.filter((row) => (row.countryCode ?? '').toUpperCase() === country)
    }

    return next
  }, [filters.country, rows])

  return (
    <section className={styles.layout}>
      <header className={styles.hero}>
        <h1 className={styles.title}>Users</h1>
        <p className={styles.subtitle}>Search users and drill into lifecycle journey events.</p>
      </header>

      <section className={styles.card}>
        <div className={styles.tabs}>
          {navLinks.map((link) => (
            <a key={link.path} href={link.href} className={styles.tabLink}>
              {link.label}
            </a>
          ))}
        </div>
        <div className={styles.row}>
          <label>
            Search
            <input
              className={styles.input}
              value={filters.query}
              onChange={(event) =>
                setFilters((current) => ({ ...current, query: event.target.value }))
              }
              placeholder="user id or email"
            />
          </label>
          <label>
            Country
            <input
              className={styles.input}
              value={filters.country}
              onChange={(event) =>
                setFilters((current) => ({ ...current, country: event.target.value }))
              }
              placeholder="US"
            />
          </label>
          <button
            className={styles.secondaryButton}
            type="button"
            onClick={() => applyAdminFilters(filters)}
          >
            Apply Filters
          </button>
          <button
            className={styles.ghostButton}
            type="button"
            onClick={() =>
              applyAdminFilters({
                ...filters,
                query: '',
                country: '',
              })
            }
          >
            Clear Search
          </button>
        </div>
      </section>

      {!token && (
        <section className={styles.card}>
          <p className={styles.emptyState}>You don't have admin access.</p>
          <a className={styles.tabLink} href="/admin">
            Go to Home
          </a>
        </section>
      )}

      {token && isLoading && (
        <section className={styles.card}>
          <p className={styles.muted}>Loading users...</p>
        </section>
      )}

      {token && error && (
        <section className={styles.card}>
          <p className={styles.error}>{error}</p>
          <button
            className={styles.secondaryButton}
            type="button"
            onClick={() => applyAdminFilters(filters)}
          >
            Retry
          </button>
        </section>
      )}

      {token && !isLoading && !error && (
        <section className={styles.card}>
          <h2>User Rows</h2>
          {filteredRows.length === 0 ? (
            <p className={styles.emptyState}>No users found.</p>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>User ID</th>
                  <th>Email</th>
                  <th>Lifecycle</th>
                  <th>Source</th>
                  <th>Country</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => {
                  const search = typeof window !== 'undefined' ? window.location.search : ''
                  return (
                    <tr key={row.userId}>
                      <td>
                        <a
                          className={styles.tabLink}
                          href={`/admin/users/${encodeURIComponent(row.userId)}${search}`}
                        >
                          {row.userId}
                        </a>
                      </td>
                      <td>{row.email}</td>
                      <td>{row.lifecycleState}</td>
                      <td>{row.firstTouchSource ?? '-'}</td>
                      <td>{row.countryCode ?? '-'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </section>
      )}
    </section>
  )
}
